import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { createUser as defaultCreateUser, CognitoUser } from '@/api/adminClient';
import { getErrorMessage } from '@/utils/error';

export interface ParsedUserRow {
  rowNumber: number;
  email: string;
  status: 'valid' | 'duplicate_in_file' | 'already_exists' | 'invalid_format';
  message: string;
  resultStatus?: 'pending' | 'processing' | 'success' | 'error';
  resultMessage?: string;
}

export interface BulkCreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingUsers: CognitoUser[];
  createUserFn?: (email: string) => Promise<unknown>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * CSV一括ユーザー作成ダイアログ
 */
const BulkCreateUserDialog: React.FC<BulkCreateUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  existingUsers,
  createUserFn = defaultCreateUser,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ParsedUserRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [isDragActive, setIsDragActive] = useState(false);

  // 既存ユーザーのメールアドレス集合（小文字で比較）
  const existingEmailsSet = useMemo(() => {
    return new Set(existingUsers.map(u => u.email.trim().toLowerCase()));
  }, [existingUsers]);

  // リセット
  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setRows([]);
    setParseError(null);
    setIsProcessing(false);
    setIsCompleted(false);
    setProgress({ current: 0, total: 0 });
  }, []);

  const handleClose = useCallback(() => {
    if (isProcessing) return; // 処理中は閉じない
    handleReset();
    onClose();
  }, [isProcessing, handleReset, onClose]);

  // テンプレートCSVダウンロード
  const handleDownloadTemplate = useCallback(() => {
    const csvContent = 'email\r\nuser1@example.com\r\nuser2@example.com\r\n';
    // Excelで文字化けしないよう UTF-8 BOM を付与
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_bulk_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // CSV解析
  const parseCsvText = useCallback((text: string): ParsedUserRow[] => {
    // 改行で分割
    const lines = text.split(/\r\n|\r|\n/);
    const parsedRows: ParsedUserRow[] = [];
    const seenInFile = new Set<string>();

    let startIndex = 0;
    // 1行目がヘッダー（email, メールアドレス等）か判定
    if (lines.length > 0) {
      const firstLineTrimmed = lines[0].trim().replace(/^["']|["']$/g, '');
      const firstCol = firstLineTrimmed.split(',')[0].trim().toLowerCase();
      if (['email', 'メールアドレス', 'mail', 'ユーザー', 'user'].includes(firstCol)) {
        startIndex = 1;
      }
    }

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // 空行スキップ

      // カンマ区切りの最初の列を取得（クォート除去）
      const columns = line.split(',');
      const rawEmail = columns[0].trim().replace(/^["']|["']$/g, '');
      const lowerEmail = rawEmail.toLowerCase();
      const rowNumber = i + 1;

      if (!rawEmail) {
        parsedRows.push({
          rowNumber,
          email: '',
          status: 'invalid_format',
          message: 'メールアドレスが空です',
        });
      } else if (!EMAIL_REGEX.test(rawEmail)) {
        parsedRows.push({
          rowNumber,
          email: rawEmail,
          status: 'invalid_format',
          message: 'メールアドレスの形式が正しくありません',
        });
      } else if (seenInFile.has(lowerEmail)) {
        parsedRows.push({
          rowNumber,
          email: rawEmail,
          status: 'duplicate_in_file',
          message: 'CSVファイル内で重複しています',
        });
      } else if (existingEmailsSet.has(lowerEmail)) {
        seenInFile.add(lowerEmail);
        parsedRows.push({
          rowNumber,
          email: rawEmail,
          status: 'already_exists',
          message: '既にシステムに登録されています',
        });
      } else {
        seenInFile.add(lowerEmail);
        parsedRows.push({
          rowNumber,
          email: rawEmail,
          status: 'valid',
          message: '登録可能',
          resultStatus: 'pending',
        });
      }
    }

    return parsedRows;
  }, [existingEmailsSet]);

  // ファイル読み込み処理 (UTF-8 または Shift-JIS)
  const processFile = useCallback(async (file: File) => {
    setSelectedFile(file);
    setParseError(null);
    setIsCompleted(false);

    try {
      const buffer = await file.arrayBuffer();
      let text = '';

      // まず UTF-8 でパースを試みる
      try {
        const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
        text = utf8Decoder.decode(buffer);
      } catch {
        // UTF-8 デコード失敗時は Shift-JIS でデコード
        const sjisDecoder = new TextDecoder('shift_jis');
        text = sjisDecoder.decode(buffer);
      }

      // BOM除去
      text = text.replace(/^\uFEFF/, '');

      const parsed = parseCsvText(text);
      if (parsed.length === 0) {
        setParseError('有効なデータ行が見つかりませんでした。ファイル内容を確認してください。');
        setRows([]);
      } else {
        setRows(parsed);
      }
    } catch (err: unknown) {
      setParseError(getErrorMessage(err, 'CSVファイルの読み込みに失敗しました'));
      setRows([]);
    }
  }, [parseCsvText]);

  // ファイル選択イベント
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  }, [processFile]);

  // ドラッグ＆ドロップイベント
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragActive(false);
  }, []);

  // 集計
  const validRows = useMemo(() => rows.filter(r => r.status === 'valid'), [rows]);
  const duplicateRows = useMemo(() => rows.filter(r => r.status === 'already_exists' || r.status === 'duplicate_in_file'), [rows]);
  const invalidRows = useMemo(() => rows.filter(r => r.status === 'invalid_format'), [rows]);

  const successCount = useMemo(() => rows.filter(r => r.resultStatus === 'success').length, [rows]);
  const failCount = useMemo(() => rows.filter(r => r.resultStatus === 'error').length, [rows]);

  // 登録実行
  const handleExecuteBulkCreate = useCallback(async () => {
    if (validRows.length === 0 || isProcessing) return;

    setIsProcessing(true);
    setProgress({ current: 0, total: validRows.length });

    let currentCompleted = 0;

    for (const targetRow of validRows) {
      // 処理中ステータスに更新
      setRows(prev =>
        prev.map(r =>
          r.rowNumber === targetRow.rowNumber
            ? { ...r, resultStatus: 'processing' }
            : r
        )
      );

      try {
        await createUserFn(targetRow.email);
        setRows(prev =>
          prev.map(r =>
            r.rowNumber === targetRow.rowNumber
              ? { ...r, resultStatus: 'success', resultMessage: '登録完了' }
              : r
          )
        );
      } catch (err: unknown) {
        const errorMsg = getErrorMessage(err, '登録失敗');
        setRows(prev =>
          prev.map(r =>
            r.rowNumber === targetRow.rowNumber
              ? { ...r, resultStatus: 'error', resultMessage: errorMsg }
              : r
          )
        );
      }

      currentCompleted += 1;
      setProgress({ current: currentCompleted, total: validRows.length });

      // Cognito のリクエスト制限を考慮し少し待機 (50ms)
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    setIsProcessing(false);
    setIsCompleted(true);
    onSuccess();
  }, [validRows, isProcessing, createUserFn, onSuccess]);

  const getStatusChip = (row: ParsedUserRow) => {
    if (isCompleted || isProcessing) {
      if (row.resultStatus === 'success') {
        return <Chip label="登録完了" size="small" color="success" />;
      }
      if (row.resultStatus === 'error') {
        return <Chip label="失敗" size="small" color="error" />;
      }
      if (row.resultStatus === 'processing') {
        return <Chip label="登録中..." size="small" color="primary" variant="outlined" />;
      }
    }

    switch (row.status) {
      case 'valid':
        return <Chip label="登録可能" size="small" color="success" variant="outlined" />;
      case 'already_exists':
        return <Chip label="既に登録済" size="small" color="warning" variant="outlined" />;
      case 'duplicate_in_file':
        return <Chip label="ファイル内重複" size="small" color="warning" variant="outlined" />;
      case 'invalid_format':
        return <Chip label="形式エラー" size="small" color="error" variant="outlined" />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="bulk-create-user-dialog-title"
    >
      <DialogTitle id="bulk-create-user-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFileIcon color="primary" />
        <Typography variant="h6" component="span" fontWeight="bold">
          ユーザーCSV一括登録
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ textTransform: 'none' }}
          >
            ひな形CSVダウンロード
          </Button>
          <IconButton size="small" onClick={handleClose} disabled={isProcessing}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        {/* ファイル未選択時: ドロップゾーン */}
        {!selectedFile && (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => document.getElementById('csv-file-input')?.click()}
          >
            <input
              id="csv-file-input"
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <CloudUploadIcon sx={{ fontSize: 56, color: 'action.active', mb: 1 }} />
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              CSVファイルをドラッグ＆ドロップ
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              または クリックしてファイルを選択
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              ※ 1列目にメールアドレスを記載したCSVファイル（UTF-8 / Shift-JIS対応）
            </Typography>
          </Box>
        )}

        {/* パースエラー */}
        {parseError && (
          <Alert severity="error" sx={{ mt: 2 }} action={
            <Button color="inherit" size="small" onClick={handleReset}>
              再選択
            </Button>
          }>
            {parseError}
          </Alert>
        )}

        {/* ファイル選択後: プレビュー画面 */}
        {selectedFile && rows.length > 0 && (
          <Box>
            {/* ファイル情報＆再選択ボタン */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  選択ファイル: {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({rows.length}行)
                </Typography>
              </Box>
              {!isProcessing && !isCompleted && (
                <Button size="small" startIcon={<RefreshIcon />} onClick={handleReset}>
                  別のCSVを選択
                </Button>
              )}
            </Box>

            {/* サマリーカード */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={`登録対象: ${validRows.length}件`}
                color="success"
                variant={validRows.length > 0 ? 'filled' : 'outlined'}
              />
              {duplicateRows.length > 0 && (
                <Chip
                  label={`重複・既存: ${duplicateRows.length}件`}
                  color="warning"
                  variant="outlined"
                />
              )}
              {invalidRows.length > 0 && (
                <Chip
                  label={`形式不正: ${invalidRows.length}件`}
                  color="error"
                  variant="outlined"
                />
              )}
            </Box>

            {/* 処理完了メッセージ */}
            {isCompleted && (
              <Alert
                severity={failCount === 0 ? 'success' : 'warning'}
                sx={{ mb: 2 }}
                icon={failCount === 0 ? <CheckCircleIcon fontSize="inherit" /> : <ErrorIcon fontSize="inherit" />}
              >
                登録処理が完了しました（成功: {successCount}件{failCount > 0 ? `、失敗: ${failCount}件` : ''}）
              </Alert>
            )}

            {/* プログレスバー */}
            {isProcessing && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    登録進行中...
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {progress.current} / {progress.total} 件 ({Math.round((progress.current / progress.total) * 100)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
                />
              </Box>
            )}

            {/* テーブル */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>行</TableCell>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell sx={{ width: 140 }}>状態</TableCell>
                    <TableCell>備考 / 結果</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.rowNumber} hover>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {row.rowNumber}
                      </TableCell>
                      <TableCell sx={{ fontWeight: row.status === 'valid' ? 500 : 400 }}>
                        {row.email || '(空)'}
                      </TableCell>
                      <TableCell>{getStatusChip(row)}</TableCell>
                      <TableCell sx={{ color: (row.status === 'invalid_format' || row.resultStatus === 'error') ? 'error.main' : 'text.secondary', fontSize: '0.8rem' }}>
                        {row.resultMessage || (row.status === 'valid' ? '―' : row.message)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isProcessing}>
          {isCompleted ? '閉じる' : 'キャンセル'}
        </Button>
        {!isCompleted && selectedFile && validRows.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteBulkCreate}
            disabled={isProcessing}
            startIcon={<UploadFileIcon />}
          >
            {isProcessing
              ? `登録中 (${progress.current}/${progress.total})`
              : `${validRows.length}件を一括登録`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkCreateUserDialog;
